import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  DEFAULT_PRESET,
  type Cue,
  type QCIssue,
  type Ruleset,
  type SubtitleDocument,
} from '../../core';
import { useSession } from '../session/context';
import { downloadText } from '../session/download';
import { applyFix, fixableCount, isFixable, runQc } from './engine';
import { ruleLabel } from './ruleLabels';
import { useCustomPresets } from './customPresetsContext';
import {
  isBuiltinPreset,
  presetExists,
  resolvePresetLabel,
  resolveRuleset,
  type PresetId,
} from './customPresets';
import {
  buildReportModel,
  reportFilename,
  reportToCsv,
  reportToHtml,
  type ReportKind,
} from './report';
import { indexOfKey, keyAt, reconcileCursor, resolvedCueIndex, stepKey } from './cursor';
import { acceptKey, partitionAccepted } from './accept';
import { QcContext, type NavTarget, type QcValue } from './context';
import { parseQcParam } from './qcParams';
import { plural } from '../text';
import { track } from '../analytics/client';
import { bucketCount } from '../analytics/catalog';

/** Debounce for the auto re-run so rapid typing doesn't thrash the engine. */
const RERUN_DEBOUNCE_MS = 200;

/** Whether the live accept-key set already equals a persisted key array. */
function sameKeys(set: Set<string>, arr: readonly string[]): boolean {
  if (set.size !== arr.length) return false;
  for (const k of arr) if (!set.has(k)) return false;
  return true;
}

const NO_PROTECTED_CUES: ReadonlySet<Cue> = new Set();

/** "Issue i of n: Label." for the cursor position in `list`, or '' when no cursor. */
function positionText(list: QCIssue[], key: string | null): string {
  const i = indexOfKey(list, key);
  return i >= 0 ? `Issue ${i + 1} of ${list.length}: ${ruleLabel(list[i]!.ruleId)}.` : '';
}

/** Join an outcome message and a cursor position into one utterance. */
function composed(message: string, position: string): string {
  return message && position ? `${message} ${position}` : message || position;
}

/**
 * The cue objects an accepted issue points at, so the whole-document fixers can
 * be told to leave them untouched (cue-forge-n3e.7). Identity-matched, so it must
 * be derived from the same `doc` that will be fixed.
 */
function protectedCues(doc: SubtitleDocument, accepted: QCIssue[]): ReadonlySet<Cue> {
  if (accepted.length === 0) return NO_PROTECTED_CUES;
  const indices = new Set(accepted.filter((i) => i.cueIndex >= 0).map((i) => i.cueIndex));
  if (indices.size === 0) return NO_PROTECTED_CUES;
  return new Set(doc.cues.filter((c) => indices.has(c.index)));
}

/**
 * Provides QC state over the shared session document. Auto-fixers re-scan the
 * whole document and introduce no new issues, so after any fix we replace the
 * session doc and the freshly re-evaluated issue list together. Once QC has
 * been run, any other document change (edits, undo/redo) re-runs the current
 * preset automatically so the issue list never goes stale.
 *
 * A "current issue" cursor (the QC review loop) is stored as a content-derived
 * KEY, never a raw index, so it survives the list mutating and route switches.
 * EVERY replacement of `issues` goes through {@link applyIssues}, which is the
 * single place the cursor is reconciled — so fix-and-advance falls out of one
 * rule. Revealing a cue (route switch + scroll) is delegated to the shell via
 * {@link NavTarget}, bumped only by intent moves (step/select/fix), never by a
 * background re-evaluation (run/edit/undo/preset).
 */
export function QcProvider({ children }: { children: ReactNode }) {
  const { loaded, setDoc, setQcAccepted } = useSession();
  const { presets: customs } = useCustomPresets();
  // Preselect from a `?qc=<preset>` deep link (cue-forge-kjc.7) if present —
  // preselection only, never an auto-run (no file is loaded yet). SSR-safe guard
  // mirrors AppShell's `initialRoute()`.
  const [preset, setPresetState] = useState<PresetId>(() =>
    typeof window === 'undefined'
      ? DEFAULT_PRESET
      : (parseQcParam(window.location.search) ?? DEFAULT_PRESET),
  );
  // The runnable ruleset for the selected preset, resolved from the live custom
  // snapshot (never reads storage). A custom id keeps the same string across an
  // in-place threshold edit, but its resolved ruleset gets a fresh identity (the
  // provider re-parses on save), so keying the re-run effect on THIS object — not
  // the id — catches an edit of the active preset (cue-forge-3r9 §6).
  const activeRuleset = useMemo(() => resolveRuleset(preset, customs), [preset, customs]);
  const [issues, setIssues] = useState<QCIssue[]>([]);
  // Issues the operator accepted as intentional (cue-forge-n3e.7): set aside,
  // excluded from `issues`/counts/cursor, surfaced in the "Accepted" group.
  const [acceptedIssues, setAcceptedIssues] = useState<QCIssue[]>([]);
  const [hasRun, setHasRun] = useState(false);
  const [runCount, setRunCount] = useState(0);
  const [fixStatus, setFixStatus] = useState('');
  // The one composed screen-reader announcement (cue-forge-drt.18). Studio's QC
  // surface renders exactly ONE live region (in the Inspector), fed by this
  // string. Any intent action that both reports an outcome AND moves the cursor
  // (fix-and-advance, fix-one, accept, run) must compose the two facts here —
  // "Fixed X on cue 3. Issue 4 of 12: Y." — never update two regions in the same
  // action, or screen readers queue stacked utterances. Background re-evaluations
  // (edit/undo/preset auto re-runs) never touch it, mirroring navTarget's
  // intent-only rule.
  const [announce, setAnnounce] = useState('');
  const [cursorKey, setCursorKey] = useState<string | null>(null);
  const [navTarget, setNavTarget] = useState<NavTarget>({ seq: 0, cueIndex: null });
  // The exact doc the current `issues` were evaluated against, so the auto
  // re-run effect can skip docs we already evaluated (e.g. fixAll/fixOne output).
  const evaluatedDoc = useRef<SubtitleDocument | null>(null);
  // The ruleset the current `issues` were evaluated against, so the resolved-
  // ruleset re-run effect (§6) skips an evaluation another path (run/setPreset)
  // already did — it acts ONLY on a genuine ruleset-identity change (an in-place
  // edit of the active custom preset), never on a plain document change.
  const evaluatedRuleset = useRef<Ruleset | null>(null);
  // Mirrors of state the cursor reconciler reads, so callbacks/effects never
  // close over a stale array or key. `prevIssuesRef` is always the array the
  // cursor currently indexes (the ACTIVE list, updated together with `issues`).
  const prevIssuesRef = useRef<QCIssue[]>([]);
  const cursorKeyRef = useRef<string | null>(null);
  // The last full (pre-partition) evaluation, so accept/unaccept can re-split it
  // into active/accepted without re-running the engine.
  const rawIssuesRef = useRef<QCIssue[]>([]);
  // The live accept-key set, mirroring `loaded.qcAccepted` (the persisted source
  // of truth). Used to partition every freshly-evaluated list.
  const acceptedKeysRef = useRef<Set<string>>(new Set());
  // The issue set the current run started from, captured on an explicit run or a
  // preset change (never on edit-driven re-eval or a fix). The report diffs it
  // against the live list to report which issues were fixed vs remain.
  const baselineRef = useRef<QCIssue[]>([]);
  // The document as it was at that run, so the report can resolve fixed issues'
  // timecodes against the cues that existed then (a fix may renumber/delete cues).
  const baselineDocRef = useRef<SubtitleDocument | null>(null);

  // The sole `setIssues` funnel: partition the freshly-evaluated list into active
  // vs accepted (cue-forge-n3e.7), reconcile the cursor against the ACTIVE list,
  // then commit all three together. `first` (used by run()) selects the first
  // active issue. Partitioning here means every consumer of `issues` (counts,
  // cursor, grid markers) subtracts accepted with no change of its own.
  const applyIssues = useCallback((next: QCIssue[], policy: 'reconcile' | 'first') => {
    rawIssuesRef.current = next;
    const { active, accepted } = partitionAccepted(
      next,
      acceptedKeysRef.current,
      evaluatedDoc.current,
    );
    const key =
      policy === 'first'
        ? active.length
          ? keyAt(active, 0)
          : null
        : reconcileCursor(prevIssuesRef.current, cursorKeyRef.current, active);
    prevIssuesRef.current = active;
    cursorKeyRef.current = key;
    setCursorKey(key);
    setIssues(active);
    setAcceptedIssues(accepted);
    return key;
  }, []);

  // Ask the shell to reveal a cue. New object identity each call so a consumer
  // effect keyed on navTarget fires once per intent move. cueIndex null = no cue.
  const bumpNav = useCallback((cueIndex: number | null) => {
    setNavTarget((t) => ({ seq: t.seq + 1, cueIndex }));
  }, []);

  // Report an intent action's outcome: the visible status line gets the message,
  // the single live region gets the message + where the cursor landed, composed
  // into ONE utterance (cue-forge-drt.18).
  const report = useCallback((message: string, position = '') => {
    setFixStatus(message);
    setAnnounce(composed(message, position));
  }, []);

  const run = useCallback(() => {
    if (!loaded) return;
    evaluatedDoc.current = loaded.doc;
    evaluatedRuleset.current = activeRuleset;
    setHasRun(true);
    setRunCount((n) => n + 1);
    const found = runQc(loaded.doc, activeRuleset);
    baselineRef.current = found;
    baselineDocRef.current = loaded.doc;
    const key = applyIssues(found, 'first');
    // The run summary + the primed cursor position, as one utterance. Announced
    // only on an EXPLICIT run — background re-evaluations never re-announce
    // (cue-forge-zxw.9) — and composed with the position so the summary and the
    // current-issue announcement can't stack (cue-forge-drt.18).
    const active = prevIssuesRef.current;
    const errors = active.filter((i) => i.severity === 'error').length;
    setAnnounce(
      composed(
        `QC complete: ${plural(errors, 'error')}, ${plural(active.length - errors, 'warning')}.`,
        positionText(active, key),
      ),
    );
    // A custom preset id can carry user free text, so it collapses to 'custom'.
    track('qc_run', {
      preset: isBuiltinPreset(preset) ? preset : 'custom',
      issues: bucketCount(active.length),
    });
  }, [loaded, activeRuleset, preset, applyIssues]);

  const setPreset = useCallback(
    (next: PresetId) => {
      setPresetState(next);
      // Keep the list in sync with the chosen preset once QC has been run. A new
      // preset is a fresh run, so reset the report baseline to its results.
      // Resolve the id here (it may name a custom preset) and record the ruleset
      // so the §6 re-run effect sees this evaluation and won't double-run.
      if (loaded && hasRun) {
        const nextRuleset = resolveRuleset(next, customs);
        evaluatedDoc.current = loaded.doc;
        evaluatedRuleset.current = nextRuleset;
        const found = runQc(loaded.doc, nextRuleset);
        baselineRef.current = found;
        baselineDocRef.current = loaded.doc;
        applyIssues(found, 'reconcile');
      }
    },
    [loaded, hasRun, customs, applyIssues],
  );

  const fixAll = useCallback(() => {
    if (!loaded || fixableCount(issues) === 0) return;
    const out = applyFix(
      loaded.doc,
      activeRuleset,
      issues.filter(isFixable),
      undefined,
      protectedCues(loaded.doc, acceptedIssues),
    );
    if (out.fixedCount > 0) {
      evaluatedDoc.current = out.doc;
      setDoc(out.doc);
      const newKey = applyIssues(out.issues, 'reconcile');
      setHasRun(true);
      report(
        `Fixed ${plural(out.fixedCount, 'issue')}.`,
        positionText(prevIssuesRef.current, newKey),
      );
      // Bulk op — reconcile the cursor into the remaining set, but don't yank
      // the route to a single cue.
      track('qc_fix', { scope: 'all', result: 'fixed' });
    } else {
      // Every selected fix declined (each would introduce a new issue): leave
      // the document untouched and say so rather than appearing to do nothing.
      // The cursor didn't move, so no position is appended.
      report('No issues could be auto-fixed.');
      track('qc_fix', { scope: 'all', result: 'unfixable' });
    }
  }, [loaded, activeRuleset, issues, acceptedIssues, setDoc, applyIssues, report]);

  // Fix a single issue. `reveal` decides whether to jump to a cue afterwards:
  // the per-card Fix fixes in place (no route yank); fix-and-advance reveals the
  // next cue so the operator keeps moving through the file.
  const applyFixToIssue = useCallback(
    (issue: QCIssue, reveal: boolean) => {
      if (!loaded || !isFixable(issue)) return;
      const where = issue.cueIndex >= 0 ? ` on cue ${issue.cueIndex}` : '';
      const label = ruleLabel(issue.ruleId);
      // Make the clicked issue current first, so the cursor advances FROM it.
      const targetKey = keyAt(issues, issues.indexOf(issue));
      cursorKeyRef.current = targetKey;
      setCursorKey(targetKey);
      const out = applyFix(
        loaded.doc,
        activeRuleset,
        [issue],
        [issue.ruleId],
        protectedCues(loaded.doc, acceptedIssues),
      );
      // A fixer re-resolves its whole rule, so it can clear other cues yet still
      // decline THIS one — the CPS assist, for instance, declines when there is
      // no gap to grow the cue into. Compare the rule's issue count and whether
      // the clicked cue cleared, so the feedback says what actually happened.
      // Compare against the RAW (pre-partition) list: `out.issues` is a full
      // re-eval, so an accepted issue of this rule must be on both sides or the
      // before/after counts would be apples-to-oranges (cue-forge-n3e.7).
      const ruleBefore = rawIssuesRef.current.filter((i) => i.ruleId === issue.ruleId).length;
      const ruleAfter = out.issues.filter((i) => i.ruleId === issue.ruleId).length;
      const clickedCleared = !out.issues.some(
        (i) => i.ruleId === issue.ruleId && i.cueIndex === issue.cueIndex,
      );
      track('qc_fix', {
        rule: issue.ruleId,
        scope: 'one',
        result: out.fixedCount > 0 && ruleAfter < ruleBefore ? 'fixed' : 'unfixable',
      });
      if (out.fixedCount > 0 && ruleAfter < ruleBefore) {
        // The fix changed the document. Commit it and re-evaluate.
        evaluatedDoc.current = out.doc;
        setDoc(out.doc);
        const newKey = applyIssues(out.issues, 'reconcile');
        setHasRun(true);
        // Compose the fix result WITH the new cursor position (cue-forge-drt.18):
        // "Fixed X on cue 3. Issue 4 of 12: Y." — one utterance, never two
        // stacked live-region updates.
        const landed = positionText(prevIssuesRef.current, newKey);
        if (clickedCleared) {
          report(`Fixed ${label}${where}.`, landed);
          // Reveal the cue now occupying the fixed issue's slot (the next cue).
          if (reveal) bumpNav(resolvedCueIndex(out.issues, newKey));
        } else {
          // Other cues of this rule were fixed, but the clicked one still needs a
          // manual edit; keep the operator on it.
          const cueRef = issue.cueIndex >= 0 ? `cue ${issue.cueIndex}` : 'this one';
          report(`Fixed other ${label} issues — ${cueRef} still needs editing.`, landed);
          if (reveal) bumpNav(issue.cueIndex >= 0 ? issue.cueIndex : null);
        }
      } else {
        // No change applied — either the engine reverted a fix that would create
        // a new issue, or the fixer declined this cue (e.g. the CPS assist found
        // no room). Leave the document untouched and say so. The cursor moved to
        // the clicked issue above, so its position joins the utterance.
        const why =
          out.fixedCount === 0
            ? ' without creating a new issue'
            : " — there's no room to adjust it";
        report(`Couldn't auto-fix ${label}${where}${why}.`, positionText(issues, targetKey));
        if (reveal) bumpNav(issue.cueIndex >= 0 ? issue.cueIndex : null);
      }
    },
    [loaded, activeRuleset, issues, acceptedIssues, setDoc, applyIssues, bumpNav, report],
  );

  // Per-card Fix: fix in place and advance the cursor, but stay put (no reveal).
  const fixOne = useCallback((issue: QCIssue) => applyFixToIssue(issue, false), [applyFixToIssue]);

  const stepNext = useCallback(() => {
    const k = stepKey(issues, cursorKeyRef.current, 1);
    cursorKeyRef.current = k;
    setCursorKey(k);
    // A pure cursor move: announce only where it landed (no outcome message,
    // and the visible fix-status line keeps its last result).
    setAnnounce(positionText(issues, k));
    bumpNav(resolvedCueIndex(issues, k));
  }, [issues, bumpNav]);

  const stepPrev = useCallback(() => {
    const k = stepKey(issues, cursorKeyRef.current, -1);
    cursorKeyRef.current = k;
    setCursorKey(k);
    setAnnounce(positionText(issues, k));
    bumpNav(resolvedCueIndex(issues, k));
  }, [issues, bumpNav]);

  const select = useCallback(
    (issue: QCIssue) => {
      const k = keyAt(issues, issues.indexOf(issue));
      cursorKeyRef.current = k;
      setCursorKey(k);
      setAnnounce(positionText(issues, k));
      bumpNav(issue.cueIndex >= 0 ? issue.cueIndex : null);
    },
    [issues, bumpNav],
  );

  const fixAndAdvance = useCallback(() => {
    // Only act on the explicitly-current issue — never an unselected one, so the
    // keyboard path agrees with the "Fix & next" button (disabled when no cursor).
    const i = indexOfKey(issues, cursorKeyRef.current);
    if (i < 0) return;
    const c = issues[i]!;
    if (isFixable(c))
      applyFixToIssue(c, true); // fix and reveal the next cue
    else stepNext(); // can't auto-fix — keep momentum, move to the next issue
  }, [issues, applyFixToIssue, stepNext]);

  // Accept an issue as intentional (cue-forge-n3e.7): add its content key, persist
  // it with the session, and re-partition the raw list so it leaves the active
  // list and the cursor advances onto the successor — like a fix, but the
  // document is untouched (no setDoc, no route yank). `issue` is an active card.
  const accept = useCallback(
    (issue: QCIssue, reveal = false) => {
      if (!loaded) return;
      const idx = issues.indexOf(issue);
      if (idx >= 0) {
        // Make the clicked issue current first, so the cursor advances FROM it.
        const targetKey = keyAt(issues, idx);
        cursorKeyRef.current = targetKey;
        setCursorKey(targetKey);
      }
      const key = acceptKey(issue, evaluatedDoc.current);
      const next = new Set(acceptedKeysRef.current);
      next.add(key);
      acceptedKeysRef.current = next;
      setQcAccepted([...next]);
      const newKey = applyIssues(rawIssuesRef.current, 'reconcile');
      // The per-card button stays put (reveal=false); the keyboard chord reveals
      // the successor's cue, an intent move mirroring fix-and-advance. After
      // applyIssues, prevIssuesRef holds the new active list the cursor landed in.
      if (reveal) bumpNav(resolvedCueIndex(prevIssuesRef.current, newKey));
      const where = issue.cueIndex >= 0 ? ` on cue ${issue.cueIndex}` : '';
      report(
        `Accepted ${ruleLabel(issue.ruleId)}${where} — won't be flagged again.`,
        positionText(prevIssuesRef.current, newKey),
      );
    },
    [loaded, issues, setQcAccepted, applyIssues, bumpNav, report],
  );

  const acceptAndAdvance = useCallback(() => {
    // Mirror fixAndAdvance: act only on the explicitly-current issue, never an
    // unselected one, so the chord agrees with the per-card Accept button.
    const i = indexOfKey(issues, cursorKeyRef.current);
    if (i < 0) return;
    accept(issues[i]!, true); // accept and reveal the next cue
  }, [issues, accept]);

  // Return a previously-accepted issue to the active list.
  const unaccept = useCallback(
    (issue: QCIssue) => {
      if (!loaded) return;
      const key = acceptKey(issue, evaluatedDoc.current);
      const next = new Set(acceptedKeysRef.current);
      next.delete(key);
      acceptedKeysRef.current = next;
      setQcAccepted([...next]);
      const newKey = applyIssues(rawIssuesRef.current, 'reconcile');
      const where = issue.cueIndex >= 0 ? ` on cue ${issue.cueIndex}` : '';
      report(
        `Restored ${ruleLabel(issue.ruleId)}${where} to the issue list.`,
        positionText(prevIssuesRef.current, newKey),
      );
    },
    [loaded, setQcAccepted, applyIssues, report],
  );

  // Export the run as a downloadable artifact (cue-forge-n3e.6). Built from the
  // live list + the run baseline, so it reflects what was fixed vs remains.
  const exportReport = useCallback(
    (kind: ReportKind) => {
      if (!loaded || !hasRun) return;
      const model = buildReportModel({
        sourceName: loaded.filename,
        presetName: resolvePresetLabel(preset, customs),
        // Never leak a device-local uuid into the shared report — a custom preset
        // travels as the stable literal token 'custom' (spec §7/§10).
        presetId: isBuiltinPreset(preset) ? preset : 'custom',
        ruleset: activeRuleset,
        doc: loaded.doc,
        baselineDoc: baselineDocRef.current ?? loaded.doc,
        issues,
        accepted: acceptedIssues,
        baseline: baselineRef.current,
        generatedAt: new Date(),
      });
      const filename = reportFilename(loaded.filename, kind);
      downloadText(
        filename,
        kind === 'csv' ? reportToCsv(model) : reportToHtml(model),
        kind === 'csv' ? 'text/csv' : 'text/html',
      );
      // Announce via the panel's status line, like fix results do. No position:
      // exporting never moves the cursor.
      report(`Exported ${filename}.`);
    },
    [loaded, hasRun, preset, customs, activeRuleset, issues, acceptedIssues, report],
  );

  const current = useMemo(() => {
    const i = indexOfKey(issues, cursorKey);
    return i >= 0 ? issues[i]! : null;
  }, [issues, cursorKey]);

  // Auto re-run: after QC has run once, any new doc identity (edit, undo/redo,
  // new load) re-evaluates the current preset. Debounced; the full run fits the
  // <1s budget. Docs produced by run/fix paths are skipped via evaluatedDoc.
  // Reconciles the cursor but never bumps navTarget (background, not an intent).
  const doc = loaded?.doc ?? null;
  useEffect(() => {
    if (!hasRun || !doc || doc === evaluatedDoc.current) return;
    const timer = setTimeout(() => {
      // A manual run()/fixOne() may have evaluated this exact doc while the
      // debounce was pending — don't evaluate the same doc twice.
      if (doc === evaluatedDoc.current) return;
      evaluatedDoc.current = doc;
      evaluatedRuleset.current = activeRuleset;
      applyIssues(runQc(doc, activeRuleset), 'reconcile');
    }, RERUN_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [doc, hasRun, activeRuleset, applyIssues]);

  // Re-run when the RESOLVED ruleset content changes, not just its id — an
  // in-place edit of the active custom preset keeps the same id but yields a new
  // ruleset identity (cue-forge-3r9 §6). Guarded on the last-evaluated ruleset so
  // run()/setPreset() (which pre-record it) don't trigger a second pass, and a
  // plain document change (handled by the debounced effect above) is a no-op here.
  useEffect(() => {
    if (!hasRun || !loaded || activeRuleset === evaluatedRuleset.current) return;
    // The custom store re-parses on every write, so saving an UNRELATED preset
    // hands the active ruleset a fresh identity with identical content. Adopt the
    // new identity without re-running — and without discarding the run baseline.
    // Rulesets are plain schema-validated JSON re-serialized on every write, so
    // stringify is a faithful content comparison here.
    if (
      evaluatedRuleset.current &&
      JSON.stringify(activeRuleset) === JSON.stringify(evaluatedRuleset.current)
    ) {
      evaluatedRuleset.current = activeRuleset;
      return;
    }
    evaluatedDoc.current = loaded.doc;
    evaluatedRuleset.current = activeRuleset;
    const found = runQc(loaded.doc, activeRuleset);
    // A genuine ruleset change is a fresh run: the report must never diff the new
    // ruleset's live issues against the old ruleset's baseline, or the exported
    // artifact claims phantom "fixed since run" rows (cue-forge-drt.10). This also
    // covers the editor's onSave, whose setPreset call still closes over the
    // pre-save customs snapshot and so evaluates the OLD (or fallback) ruleset.
    baselineRef.current = found;
    baselineDocRef.current = loaded.doc;
    applyIssues(found, 'reconcile');
  }, [activeRuleset, hasRun, loaded, applyIssues]);

  // Orphan-id coercion (§6): if the selected preset no longer resolves (a custom
  // preset deleted here or in another tab), fall back to the default so the
  // controlled <select> never points at a missing <option>. Route through
  // setPreset — not raw state — so the re-run resets the report baseline, and
  // announce the switch: results the operator is looking at must never silently
  // become Generic-based (cue-forge-drt.10).
  useEffect(() => {
    if (presetExists(preset, customs)) return;
    setPreset(DEFAULT_PRESET);
    report(
      `The selected preset was deleted — switched to ${resolvePresetLabel(DEFAULT_PRESET, customs)}.`,
    );
  }, [preset, customs, setPreset, report]);

  // Adopt the persisted accept set whenever it changes from OUTSIDE this provider
  // (a new file load resets it to none, a restore/undo/redo brings a doc's set
  // back). accept()/unaccept() update the ref synchronously, so when their own
  // setQcAccepted round-trips here the set already matches and we skip — only a
  // genuine external change re-partitions. A fresh load is partitioned by the
  // auto-rerun above (guarded on the doc matching what the raw list belongs to).
  const persistedAccepts = loaded?.qcAccepted;
  useEffect(() => {
    const nextKeys = persistedAccepts ?? [];
    if (sameKeys(acceptedKeysRef.current, nextKeys)) return;
    acceptedKeysRef.current = new Set(nextKeys);
    if (hasRun && loaded && evaluatedDoc.current === loaded.doc) {
      applyIssues(rawIssuesRef.current, 'reconcile');
    }
  }, [persistedAccepts, loaded, hasRun, applyIssues]);

  const value = useMemo<QcValue>(
    () => ({
      preset,
      setPreset,
      issues,
      accepted: acceptedIssues,
      accept,
      unaccept,
      hasRun,
      runCount,
      run,
      fixAll,
      fixOne,
      fixStatus,
      announce,
      cursorKey,
      current,
      stepNext,
      stepPrev,
      select,
      fixAndAdvance,
      acceptAndAdvance,
      navTarget,
      exportReport,
    }),
    [
      preset,
      setPreset,
      issues,
      acceptedIssues,
      accept,
      unaccept,
      hasRun,
      runCount,
      run,
      fixAll,
      fixOne,
      fixStatus,
      announce,
      cursorKey,
      current,
      stepNext,
      stepPrev,
      select,
      fixAndAdvance,
      acceptAndAdvance,
      navTarget,
      exportReport,
    ],
  );

  return <QcContext.Provider value={value}>{children}</QcContext.Provider>;
}
